import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { recentsKeys } from '~/lib/query-keys';

/** Mirrors `RECENT_OPEN_KINDS` (`src/recents/store.ts`) by hand — the
 *  frontend can't import a backend module. */
export type RecentOpenKind = 'project' | 'file' | 'document' | 'app';

/**
 * `POST /api/recents/open` — fire-and-forget record that the caller just
 * opened one item, from the palette's own recorded choke points (FRONT-2391:
 * `work-detail.tsx`, `files.tsx`'s `FileDetail`/`DocumentDetail`,
 * `inbox-chat-modal.tsx`'s file/document branches, `apps-edit.tsx`).
 *
 * Exported on its own (not just via the `useRecordOpen` hook below) so a
 * call site that already has its own effect/guard can post directly instead
 * of composing two effect hooks.
 */
export function recordOpen(kind: RecentOpenKind, key: string): Promise<void> {
  return api.post<void>('/api/recents/open', { kind, key }).then(() => undefined);
}

/**
 * Fires `recordOpen` exactly once per (kind, key) for the life of this
 * component instance.
 *
 * Guarded by a REF, not by component-mount bookkeeping, for two reasons:
 * React 18 StrictMode deliberately double-invokes effects in dev (mount,
 * cleanup, mount again) without actually unmounting the component — a ref
 * survives that double-invoke and a `useState`-based "have I fired" flag
 * would too, but a ref needs no extra render; and a re-render that leaves
 * `kind`/`key` unchanged (e.g. an unrelated parent state update) must not
 * re-POST either, which the effect's own dependency array already handles —
 * the ref only has to survive the double-invoke.
 *
 * When `key` genuinely CHANGES while the component stays mounted (e.g. a
 * shared detail component reused across items without a `key` prop reset),
 * the effect re-runs (its own deps changed) and the ref reflects the new key,
 * so it records the new one and would not re-fire for the same (kind, key) twice.
 *
 * `key` is `undefined` while the id/slug/filename identifying the item isn't
 * known yet (e.g. a route still resolving `useParams()`) — the hook no-ops
 * until it is, and never fires for an empty string either.
 *
 * Never throws and never toasts: this is a "recent activity" convenience
 * list, not a user-facing action, so a dropped write here must never surface
 * as an error the person has to see or dismiss — worst case a row shows up a
 * beat late next time the palette opens.
 */
/**
 * The identity one open is deduplicated on — the (kind, key) PAIR, never the
 * key alone: a project id and a file id can be the same string, so a component
 * whose kind changes while its key stays put must still record the new open.
 * Returns `null` when there is nothing to record yet.
 */
export function recordOpenToken(kind: RecentOpenKind, key: string | undefined): string | null {
  return key ? `${kind}\u0000${key}` : null;
}

export function useRecordOpen(kind: RecentOpenKind, key: string | undefined): void {
  const firedForRef = useRef<string | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    const token = recordOpenToken(kind, key);
    if (!token || !key) return;
    if (firedForRef.current === token) return;
    firedForRef.current = token;
    recordOpen(kind, key)
      .then(() => {
        qc.invalidateQueries({ queryKey: recentsKeys.all });
      })
      .catch((err) => {
        // Silent by design — see the module docblock above.
        console.debug('[recents] failed to record open', kind, err);
      });
  }, [kind, key, qc]);
}
